import { isEditableElement } from '../../hooks/ui/viewportFrame';
import { revealScrollerFor as nearestScrollableAncestor } from './modalRevealScroller';
import { kbLog } from './keyboardReframeDebug';
import {
  releaseSpacer,
  hasLiveInlineSpacer,
  hasParkedSpacers,
  isInlineSpacerHeld,
  inlineSpacerScroller,
  parkSpacer,
  cancelAllSettles,
  releaseAllParkedSpacers,
  releaseParkedSpacer,
} from './inlineKeyboardSpacer';
import { settleParkedSpacers } from './inlineKeyboardSettle';
import {
  startRestoreGlide, restoreGlideScroller, takeRestoreGlide, cancelRestoreGlide,
} from './inlineRestoreGlide';
import { fillRevealSpacer } from './inlineRevealSpacerFill';
import {
  isShrunkScroller, restorePolicy, parkLiveSpacerIfStays, dropForeignSpacer,
} from './inlineKeyboardRestorePolicy';
import { markProgrammaticScroll } from './scrollTapGuard';
import {
  canPredictKeyboard,
  createKeyboardPresenceProbe,
  installUserGestureFocusTracking,
  isCoarsePointer,
  isUserGestureFocus,
} from './keyboardPresence';
import {
  computeInlineRevealPlan,
  isGuardedSurfacePath,
  keyboardKindFor,
  predictKeyboardTop,
} from './inlineRevealPlan';

// The PURE policy layer (the centring law, the plan math, keyboard-height
// prediction + persistence, the surface gate) lives in inlineRevealPlan.js —
// re-exported here so every existing importer (tests, useVisualViewportFrame)
// keeps this module as the single entry point. This file owns only the DOM:
// the installer, the reveal application, the spacer, and the restore machinery.
export {
  computeInlineRevealPlan,
  isGuardedSurfacePath,
  predictKeyboardTop,
  recordKeyboardHeight,
  keyboardKindFor,
  INLINE_REVEAL_SAFETY_PX,
  INLINE_REVEAL_MAX_SPACER_PX,
  INLINE_REVEAL_TOP_INSET_PX,
} from './inlineRevealPlan';

/**
 * Global "inline field about to be covered by the incoming keyboard" guard
 * (mirrors `scrollTapGuard.js`'s architecture: a pure decision fn + a
 * document-level installer).
 *
 * Why this exists (device-log anchored, 2026-08-20): focusing an INLINE
 * (non-modal) input that sits low on screen makes iOS pan the ENTIRE app —
 * `focusin id=cobrar-search-input → +104 vv.resize ot=105.7 + doc.scroll
 * y=106 → snapped back y=0 at +121 but vv ot animates back until +204`. The
 * document snap-back (`AppShell.js`'s existing pin) already works; the
 * VISUAL VIEWPORT pan is compositor-animated and cannot be programmatically
 * reset once it starts. **The only real fix is PREVENTION**: scroll the
 * field's own container so the field is already visible ABOVE where the
 * keyboard will land, at focus time — before iOS ever decides it needs to
 * pan the page to reveal the caret. Fields that sit high (Catalog, Clients
 * search) never trigger the bug; Estimates/Cobrar/Drafts do — it's
 * POSITION-dependent, which is why it reads as inconsistent to the founder.
 *
 * ROUND 6 (2026-08-20, same day): the mechanism above is right; the THRESHOLD
 * shipped with it was wrong, and fresh captures proved it inert. On Cobrar and
 * Estimates the guard logged `inlineRevealSkip reason=visible` — it judged the
 * field already clear — and iOS panned 105.7px / 114.7px regardless. Being
 * "above the keyboard line" is not enough for iOS.
 *
 * ROUND 7 (2026-08-21) closed it: iOS CENTRES the focused field in the visible
 * band, so the target is `predictedKeyboardTop / 2`, not any constant margin —
 * see `computeInlineRevealPlan` for the five captures that pin the law. Round
 * 6's 160px guess was itself falsified the same way round 5's 16px was. The
 * lesson worth keeping: every constant here was wrong until it was MEASURED,
 * and two of the three wrong answers looked reasonable.
 *
 * Mechanism: one document-level CAPTURE-phase `focusin` listener (same
 * "capture on `document`, ahead of React's delegated handlers" shape as
 * `scrollTapGuard.js`). On focus of an editable that ISN'T inside an exempt
 * subtree (modals + the AI panel mobile footer already run their own
 * keyboard-reveal machinery — see `EXEMPT_SELECTOR`), find the nearest
 * `overflow:auto|scroll` ancestor and, if the field would land under the
 * PREDICTED keyboard line, scroll that container so it clears — SYNCHRONOUSLY,
 * inside the handler, so the layout is already correct before iOS evaluates
 * where the caret sits. A scroll we performed is a LOAN: it's restored (smooth)
 * once focus genuinely leaves and the user hasn't scrolled the container
 * themselves since (same restore contract as `useKeyboardFieldReveal`).
 *
 * The decision math is pure/DOM-free (`computeInlineRevealPlan`,
 * `predictKeyboardTop`) so it's exhaustively unit-tested without a real
 * device; only the installer touches the DOM.
 */

/** Attribute a subtree carries to opt OUT of this global guard. */
export const EXEMPT_SELECTOR = '[data-cvx-kb-guard-exempt]';

// KAN-930 restore policy (stay vs restore, park, rest/shrunk markers):
// inlineKeyboardRestorePolicy.js. Re-exported for existing importers.
export { SCROLL_STAYS_SELECTOR, KB_REST_SELECTOR } from './inlineKeyboardRestorePolicy';

/** User-scroll tolerance: beyond this from our landing spot = user's intent, don't restore. */
const RESTORE_DRIFT_PX = 8;

/**
 * Delay before checking whether focus truly left (vs. moved to another
 * field) before restoring. Round-5 P0: this used to be 0ms, so a
 * search→tap-a-row sequence raced the restore's smooth scroll against the
 * user's finger (rows shifted mid-tap) AND got recorded as a "just
 * scrolled" container by `scrollTapGuard`, which then swallowed the row's
 * click. 350ms clears `scrollTapGuard`'s own 150ms suppression window with
 * margin, so by the time we'd restore, any tap has already resolved.
 */
const RESTORE_DELAY_MS = 350;

/** Smooth restores fire many `scroll` events over their whole animation — mark for longer. */
const RESTORE_SCROLL_MARK_MS = 600;

/** How long to let a smooth restore land before dropping the spacer padding. */
const SPACER_RELEASE_DELAY_MS = 500;

/** `innerHeight - visualViewport.height` below this = keyboard is gone (tolerates Safari's accessory-bar residue). */
const KEYBOARD_GONE_TOLERANCE_PX = 40;

/**
 * Keyboard-still-dismissing retry cadence. Device logs put the viewport's
 * restore 400–750ms AFTER focusout, so a single check at RESTORE_DELAY_MS
 * (350ms) usually lands while the keyboard is still up — returning without
 * re-arming left the loan permanently unreturned (list stayed scrolled).
 * Retry every 150ms up to 8 times (350 + 8×150 = 1550ms > the 750ms worst
 * case observed); give up after that — a keyboard that never leaves means
 * the user is still engaged somewhere.
 */
const RESTORE_RETRY_MS = 150;
const RESTORE_MAX_RETRIES = 8;

let installed = false;
let activeCleanup = null;

/**
 * `{ scroller, beforeTop, afterTop }` for the reveal we most recently performed
 * and haven't restored yet — the restore contract on close.
 *
 * MODULE scope, not installer-closure scope, so `takeOverInlineReveal` can hand
 * the loan to another owner. See that function for why that matters.
 */
let restoreState = null;

/**
 * Settle any loan (scroll + spacer) still held on a DIFFERENT scroller than
 * `current`, instantly. Called when a new reveal is about to target `current`:
 * `restoreState`/the spacer each track ONE scroller, so without this the
 * new reveal would OVERWRITE the old loan and the previous container would be
 * left permanently scrolled (and permanently padded — dead space at the
 * bottom of its list). Real founder flow: Cobrar search (loan on the tab
 * scroller) → open the add-customer drawer → focus a drawer field (loan on
 * the drawer's scroller). Instant, not smooth: the old container is behind an
 * overlay or off-attention at this moment. Same drift check as the timed
 * restore — a user-scrolled container keeps the user's position.
 */
function settleForeignLoan(current) {
  const r = restoreState;
  if (r && r.scroller !== current) {
    restoreState = null;
    if (restorePolicy(r.scroller) === 'stay') {
      kbLog('inlineRevealForeignStay', {});
    } else if (Math.abs(r.scroller.scrollTop - r.afterTop) <= RESTORE_DRIFT_PX) {
      markProgrammaticScroll(r.scroller, RESTORE_SCROLL_MARK_MS);
      r.scroller.scrollTop = r.beforeTop;
      kbLog('inlineRevealForeignSettle', { to: r.beforeTop });
    }
  }
  dropForeignSpacer(current);
}

const noop = () => {};

/** A page's glide-back landed: drop ITS spacers (live and parked), nothing else. */
function releaseGlideSpacers(scroller) {
  if (inlineSpacerScroller() === scroller && !isInlineSpacerHeld()) releaseSpacer();
  releaseParkedSpacer(scroller);
}

/**
 * Transfer an in-flight reveal loan on `scroller` to a different owner, and
 * return the ORIGINAL pre-reveal scrollTop so the new owner restores all the
 * way back to true rest.
 *
 * Why this exists (code review, 2026-08-20): when this guard's raise is
 * headroom-limited it only PARTIALLY clears the field. 250ms later
 * `useKeyboardFieldReveal` measures the field, still finds it under the
 * keyboard, and scrolls again — recording `beforeTop = scroller.scrollTop`,
 * which by then is OUR raised position, not the true resting one. That left
 * two uncoordinated restore chains on different clocks (ours: 350ms +
 * visualViewport-keyed retries; theirs: on `keyboardOpen` going false) racing
 * over one scroller, and our own drift check could then skip our restore —
 * stranding the list at an intermediate position that never settles back.
 *
 * Handing the loan over collapses that to exactly ONE restore, to the right
 * place. Returns `null` when we hold no loan on that scroller, in which case
 * the caller owns its scroll outright and should record its own `beforeTop`.
 */
export function takeOverInlineReveal(scroller) {
  const r = restoreState;
  if (!r || r.scroller !== scroller) return null;
  restoreState = null; // a pending attemptRestore no-ops on a null state
  kbLog('inlineRevealHandoff', { beforeTop: r.beforeTop, afterTop: r.afterTop });
  return { beforeTop: r.beforeTop };
}

/**
 * Install the guard once. Idempotent (returns the existing cleanup if already on).
 * @param {Document} [docArg] inject a document for tests
 * @returns {() => void} cleanup
 */
export function installInlineKeyboardGuard(docArg) {
  // Mirrors scrollTapGuard's own guard pattern: `typeof document` is checked
  // BEFORE `document` is ever dereferenced. A `doc = document` DEFAULT PARAM
  // would evaluate that bare identifier at call time — before this guard
  // ever runs — and throw in a document-less environment (SSR/Node). No
  // default param here; resolve the target doc only after confirming a
  // global `document` exists.
  if (typeof document === 'undefined') return noop;
  const doc = docArg || document;
  if (installed) return activeCleanup || noop;

  let restoreTimer = 0;
  let restoreRetries = 0;
  // Learns whether this device actually raises a software keyboard. Armed on
  // EVERY coarse-pointer focus — including while suppressed — because a `hit`
  // is how a tablet recovers the instant its keyboard is detached.
  const presenceProbe = createKeyboardPresenceProbe(doc);
  const stopGesture = installUserGestureFocusTracking();

  // Restore ONLY when ALL of these hold (round-5 P0 fix — see RESTORE_DELAY_MS):
  //  1. RESTORE_DELAY_MS has elapsed since focusout (checked by the setTimeout itself).
  //  2. No editable currently holds focus (else this is a hop to another
  //     field — a new session; that field's own focusin already ran its
  //     OWN reveal check, so just leave this restore pending for later).
  //  3. The keyboard is actually gone (visualViewport height back near
  //     innerHeight) — a focusout can fire well before the keyboard's own
  //     dismiss animation finishes.
  //  4. The user hasn't scrolled the container since (drift check).
  const attemptRestore = () => {
    const r = restoreState;
    // A handed-over scroll loan (takeOverInlineReveal) leaves restoreState null
    // while the SPACER is still ours to clean up — so run for either (and for
    // a parked spacer, which must settle on this keyboard close).
    if (!r && (!hasLiveInlineSpacer() || isInlineSpacerHeld()) && !hasParkedSpacers()) return;
    if (isEditableElement(doc.activeElement)) return; // (2)
    const keyboardGone = window.visualViewport
      ? (window.innerHeight - window.visualViewport.height) < KEYBOARD_GONE_TOLERANCE_PX
      : true;
    if (!keyboardGone) { // (3) — still dismissing: re-arm (see RESTORE_RETRY_MS)
      if (restoreRetries < RESTORE_MAX_RETRIES) {
        restoreRetries += 1;
        restoreTimer = setTimeout(attemptRestore, RESTORE_RETRY_MS);
      }
      return;
    }
    restoreState = null;
    let restoring = false;
    const stays = r && restorePolicy(r.scroller) === 'stay';
    if (stays) kbLog('inlineRevealStay', { top: r.afterTop });
    if (r && !stays && Math.abs(r.scroller.scrollTop - r.afterTop) <= RESTORE_DRIFT_PX) { // (4)
      markProgrammaticScroll(r.scroller, RESTORE_SCROLL_MARK_MS);
      kbLog('inlineRevealRestore', { from: r.afterTop, to: r.beforeTop });
      r.scroller.scrollTo({ top: r.beforeTop, behavior: 'smooth' });
      restoring = true;
    }
    // Drop the spacer only AFTER the smooth restore has landed. Removing the
    // padding shrinks scrollHeight, which clamps scrollTop instantly — do that
    // mid-animation and the list visibly jumps before finishing its glide.
    // Founder feedback 2026-10-22 (replaces KAN-930's "park until close"): a
    // stay scroller keeps its scroll only while that shows no blank space —
    // its spacer (and any parked one) SETTLES away in one glide
    // (inlineKeyboardSettle.js). A page whose restore was skipped (user
    // drift) settles the same way instead of dropping the padding at once
    // (that clamp was a snap).
    // A page gliding back keeps BOTH its spacers (live + parked) until the glide
    // lands and is skipped by the settle (inlineRestoreGlide) — one motion.
    if (restoring) startRestoreGlide(r.scroller, r.beforeTop, releaseGlideSpacers, SPACER_RELEASE_DELAY_MS);
    if (isInlineSpacerHeld()) { settleParkedSpacers(restoreGlideScroller()); return; } // the close choreography releases the held one
    if (!parkLiveSpacerIfStays() && !restoring) { // restoring: released when the glide lands
      if (r) parkSpacer(); // our own loan, user drifted: settle (no-op if no spacer)
      else releaseSpacer(); // handed-over loan: its new owner scrolls — unchanged
    }
    settleParkedSpacers(restoreGlideScroller());
  };

  const onFocusOut = () => {
    if (!restoreState && (!hasLiveInlineSpacer() || isInlineSpacerHeld()) && !hasParkedSpacers()) return;
    clearTimeout(restoreTimer);
    restoreRetries = 0;
    restoreTimer = setTimeout(attemptRestore, RESTORE_DELAY_MS);
  };

  const onFocusIn = (e) => {
    const el = e.target;
    if (!isEditableElement(el)) return;
    // Same rule as modal anticipation: a programmatic focus does not open
    // the keyboard, so scrolling to "make room" for one just shifts the
    // page and then restores it.
    if (!isUserGestureFocus(e)) return;
    cancelAllSettles(); // a re-focus stops a settle glide where it is (2026-10-22)
    // Fine pointers need no learning — they are decided statically below.
    if (isCoarsePointer()) presenceProbe.schedule();
    // THE PREDICTION GATE. This reveal is a GUESS about a keyboard that has
    // not been measured, so it may only run where a keyboard can actually
    // appear: never on desktop, and not on a tablet that has taught us it has
    // a hardware keyboard attached. See `keyboardPresence.js` for why this is
    // a learned signal rather than a bare media query.
    if (!canPredictKeyboard()) {
      kbLog('inlineRevealSkip', { reason: 'no-software-keyboard' });
      return;
    }
    if (!isGuardedSurfacePath(window.location.pathname)) {
      kbLog('inlineRevealSkip', { reason: 'off-surface' });
      return;
    }
    if (typeof el.closest === 'function' && el.closest(EXEMPT_SELECTOR)) {
      kbLog('inlineRevealSkip', { reason: 'exempt' });
      return;
    }
    const scroller = nearestScrollableAncestor(el);
    if (!scroller) {
      kbLog('inlineRevealSkip', { reason: 'no-scroller' });
      return;
    }

    // BEFORE measuring: settle any loan still held on a different scroller
    // (its instant restore shifts layout, so rects must be read after it).
    settleForeignLoan(scroller);

    const fieldRect = el.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const kbTop = predictKeyboardTop(window.innerHeight, keyboardKindFor(el));
    const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
    // KAN-930: a body that `modalBodyShrink` shrank plans with NO spacer cap,
    // so the target matches the shrink's plan (same predicted keyboard).
    const shrunk = isShrunkScroller(scroller);

    const plan = computeInlineRevealPlan({
      fieldTop: fieldRect.top,
      fieldBottom: fieldRect.bottom,
      scrollerTop: scrollerRect.top,
      scrollerScrollTop: scroller.scrollTop,
      scrollerMaxScrollTop: maxScrollTop,
      predictedKeyboardTop: kbTop,
      ...(shrunk ? { maxSpacerPx: Infinity } : null),
    });

    if (plan == null) {
      // ft/st make a skip self-diagnosing from a pasted log: with kbTop they
      // reconstruct the centring math offline (rounds 5–7 each burned a
      // device round-trip re-deriving exactly these numbers).
      kbLog('inlineRevealSkip', {
        reason: 'centred',
        kbTop,
        ft: Math.round(fieldRect.top),
        st: Math.round(scrollerRect.top),
      });
      return;
    }
    // KAN-930: a SHRUNK body (rest pop-up, `data-cvx-kb-shrunk`) never takes a
    // spacer — the shrink made the range. Reveal as far as the real range
    // allows; a short landing is logged, never "fixed" with padding. A rest
    // card whose shrink FAILED carries no marker → today's spacer path.
    const inRest = plan.spacer > 0 && shrunk;
    const target = inRest ? Math.min(plan.scrollTop, Math.max(0, maxScrollTop)) : plan.scrollTop;
    if (inRest) kbLog('inlineRevealShort', { reason: 'rest-no-spacer', want: plan.scrollTop, target });

    // Manufacture the missing scroll range, if any (inlineRevealSpacerFill.js).
    if (plan.spacer > 0 && !inRest && !fillRevealSpacer(scroller, plan, target)) return;

    // A chain of reveals across sibling fields in the SAME scroller must
    // restore all the way back to the TRUE resting position, not just to
    // the previous field's already-shifted spot — so keep the original
    // `beforeTop` when re-revealing within the same container.
    // A glide-back still in flight on this scroller hands over its resting top
    // (and its spacer: the pending release is cancelled — inlineRestoreGlide).
    const glideTop = takeRestoreGlide(scroller);
    const beforeTop = (restoreState && restoreState.scroller === scroller)
      ? restoreState.beforeTop
      : (glideTop != null ? glideTop : scroller.scrollTop);

    clearTimeout(restoreTimer); // this reveal supersedes any pending restore-check
    markProgrammaticScroll(scroller); // else scrollTapGuard records OUR scroll as a user fling
    scroller.scrollTop = target; // SYNCHRONOUS — must land before iOS reads the caret position; NEVER smooth here

    // Verify-only: with the exact top-up above, a short landing is a genuinely
    // novel layout — log it (partial reveal still shrinks the pan) so the
    // next capture names it; never "correct" blindly again.
    const landed = scroller.scrollTop;
    if (landed < target - 1) {
      kbLog('inlineRevealShort', { landed: Math.round(landed), target });
    }

    // Anchor the restore contract on where the scroll ACTUALLY landed —
    // anchoring on the requested target would fail the restore's drift check
    // whenever a landing fell short, leaking the loan.
    //
    // The reveal is INSTANT and stays that way. A flag-gated FLIP-glide
    // experiment (`?kbglide=1`) lived here from 2026-08-21 and was deleted
    // 2026-08-24 unrun: it existed to answer whether iOS's focus sampler reads
    // the layout position or the rendered rect, that device capture was never
    // taken, and an untested branch sitting in the live reveal path — with a
    // known cost (`position: sticky` descendants lose stickiness while a
    // transform is on their ancestor) — is worse than no experiment. If the
    // jump ever needs softening, re-derive it from a capture; do not restore
    // the old module from git on the assumption it was close.
    // One restore path: while the modal close choreography HOLDS this
    // scroller's spacer it owns the scroll-back, so a field-to-field re-reveal
    // must not start a second restore chain of its own.
    if (!(isInlineSpacerHeld() && inlineSpacerScroller() === scroller)) {
      restoreState = { scroller, beforeTop, afterTop: landed };
    }
    // ft/st/max/sp: enough to replay the whole plan offline from one pasted
    // line (field top, scroller top, scroll range, spacer added).
    kbLog('inlineReveal', {
      from: beforeTop,
      to: target,
      kbTop,
      ft: Math.round(fieldRect.top),
      st: Math.round(scrollerRect.top),
      max: Math.round(maxScrollTop),
      sp: plan.spacer,
    });
  };

  doc.addEventListener('focusin', onFocusIn, true);
  doc.addEventListener('focusout', onFocusOut, true);

  installed = true;
  activeCleanup = () => {
    doc.removeEventListener('focusin', onFocusIn, true);
    doc.removeEventListener('focusout', onFocusOut, true);
    clearTimeout(restoreTimer);
    presenceProbe.cancel();
    stopGesture();
    restoreState = null;
    cancelRestoreGlide();
    releaseSpacer();
    releaseAllParkedSpacers();
    installed = false;
    activeCleanup = null;
  };
  return activeCleanup;
}
